import { useMutation, useSuspenseQuery } from '@apollo/client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Content,
  Header,
  Heading,
  Separator,
  SidebarTrigger,
  Stack,
  useToast,
} from '@eluve/components';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import {
  DynamicArtifactTemplate,
  dynamicArtifactTemplateSchema,
} from '@eluve/llm-outputs';
import {
  DynamicArtifactTemplateBuilder,
  templateBuilderStore,
} from '@eluve/smart-blocks';

import { DynamicArtifactTemplatePreview } from './components/DynamicArtifactPreview';
import { PrefillTemplateSection } from './components/PrefillTemplateSection';
import { TemplateEditingPageHeaderButtons } from './components/TemplateEditingPageHeaderButtons';
import {
  createOutputTemplateVariantMutation,
  getBaseOutputTemplateQuery,
  getOutputTemplateVariantQuery,
  updateTemplateVariantMutation,
} from './operations';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';
import { useIsTemplateDraft } from './useIsTemplateDraft';

export const CreateOutputTemplateVariantPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();
  const isEditDraftRoute = location.pathname.includes('/edit/');

  const { outputTemplateId, variantNumber } = useParams() as {
    outputTemplateId: string;
    variantNumber: string;
  };
  const isOrganizationRoute = useIsOrganizationRoute();

  useSuspenseQuery(getBaseOutputTemplateQuery, {
    variables: {
      id: outputTemplateId,
    },
  });

  const navigate = useNavigate();
  const navigateTo = `../${outputTemplateId}/variant/${variantNumber}`;

  const { isDraft } = useIsTemplateDraft(outputTemplateId);

  const [isValid, setIsValid] = useState(false);

  const handleIsValidChange = useCallback((valid: boolean) => {
    setIsValid(valid);
  }, []);

  const [createOutputTemplateVariant] = useMutation(
    createOutputTemplateVariantMutation,
    {
      onCompleted: ({ insertLlmOutputTemplateVariantsOne: template }) => {
        templateBuilderStore.send({ type: 'reset' });
        if (template) {
          navigate(navigateTo);
        }
      },
      onError: () => toast.error(t('outputTemplates.variant.createFailed')),
    },
  );

  const [updateTemplateVariant] = useMutation(updateTemplateVariantMutation, {
    onCompleted: () => {
      templateBuilderStore.send({ type: 'reset' });
      if (outputTemplateId) {
        navigate(navigateTo);
      }
    },
  });

  const handleSubmit = async () => {
    const { context } = templateBuilderStore.getSnapshot();
    const dynamicArtifact: DynamicArtifactTemplate = {
      name: context.name,
      description: context.description,
      blocks: context.blocks,
    };

    if (isEditDraftRoute) {
      await updateTemplateVariant({
        variables: {
          outputTemplateId,
          variantNumber: Number(variantNumber),
          isDraft: false,
          template: dynamicArtifact,
          isCurrentDefault: isDraft,
        },
      });
    } else {
      await createOutputTemplateVariant({
        variables: {
          outputTemplateId,
          template: dynamicArtifact,
          variantNotes: context.variantNotes,
          isDraft: false,
        },
      });
    }
  };

  const handleSaveDraft = async () => {
    const { context } = templateBuilderStore.getSnapshot();
    const dynamicArtifact: DynamicArtifactTemplate = {
      name: context.name,
      description: context.description,
      blocks: context.blocks,
    };

    if (isEditDraftRoute) {
      await updateTemplateVariant({
        variables: {
          outputTemplateId,
          variantNumber: Number(variantNumber),
          isDraft: true,
          template: dynamicArtifact,
          isCurrentDefault: false,
        },
      });
    } else {
      await createOutputTemplateVariant({
        variables: {
          outputTemplateId,
          template: dynamicArtifact,
          variantNotes: context.variantNotes,
          isDraft: true,
        },
      });
    }
  };

  const {
    data: { llmOutputTemplatesByPk: baseTemplateData },
  } = useSuspenseQuery(getOutputTemplateVariantQuery, {
    variables: {
      id: outputTemplateId,
      variantNumber: Number(variantNumber),
    },
  });

  const baseTemplate = useMemo(() => {
    const parsed = dynamicArtifactTemplateSchema.safeParse(
      baseTemplateData?.variants?.[0]?.template,
    );
    if (!parsed.success) {
      return undefined;
    }

    return parsed.data;
  }, [baseTemplateData]);

  useEffect(() => {
    if (baseTemplate && baseTemplateData) {
      templateBuilderStore.send({
        type: 'hydrateArtifact',
        artifactTemplate: baseTemplate,
        outputType:
          baseTemplateData?.llmOutputType ?? LlmOutputTypesLookup.SOAP_NOTE,
      });
      // Set the parent template's name and description for variants
      templateBuilderStore.send({
        type: 'setName',
        name: baseTemplateData.name ?? '',
      });
      templateBuilderStore.send({
        type: 'setDescription',
        description: baseTemplateData.description ?? '',
      });
    }
  }, [baseTemplate, baseTemplateData]);

  return (
    <>
      <Header>
        {isOrganizationRoute && <SidebarTrigger />}
        <div className="my-2 w-full py-2">
          <Heading>{t('outputTemplates.variant.title')}</Heading>
        </div>

        <TemplateEditingPageHeaderButtons
          handleSaveDraft={handleSaveDraft}
          handleSubmit={handleSubmit}
          handleDeleteDraft={() => navigate(navigateTo)}
          saveDraftDisabled={!isValid}
          submitDisabled={!isValid}
        />
      </Header>
      <Content>
        <Stack
          align="stretch"
          className="divide-x"
          gap={2}
          direction="horizontal"
          fullHeight
        >
          <Stack
            className="min-w-0 flex-1 overflow-y-auto p-6"
            gap={6}
            fullHeight
          >
            <PrefillTemplateSection />
            <Separator />
            <DynamicArtifactTemplateBuilder
              onIsValidChange={handleIsValidChange}
              showJsonSchema
              hideTemplateDetails={true}
            />
          </Stack>
          <Stack
            className="min-w-0 flex-1 overflow-y-auto bg-bg-tertiary p-6"
            gap={2}
            fullHeight
          >
            <DynamicArtifactTemplatePreview />
          </Stack>
        </Stack>
      </Content>
    </>
  );
};
